import { clsx } from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { StickyDetector } from "@/ui/sticky-detector/StickyDetector";
import { useInstagramSearch } from "../../hooks/useInstagramSearch";
import type { InstagramSearch } from "../../types";
import { InstagramSearchListsAssignMenu } from "../instagram-search-lists-assign-menu/InstagramSearchListsAssignMenu";
import { InstagramSearchSelectionCount } from "../instagram-search-selection-count/InstagramSearchSelectionCount";
import {
  InstagramSearchListDownload,
  InstagramSearchListsSelectionDownload,
  InstagramSearchSelectionDownload,
} from "../instagram-search-selection-download/InstagramSearchSelectionDownload";
import { InstagramSearchSelectionSelect } from "../instagram-search-selection-select/InstagramSearchSelectionSelect";
import styles from "./InstagramSearchSelection.module.css";

interface InstagramSearchProfilesSelectionProps {
  profiles: InstagramSearch.Profile[];
  loading: boolean;
}

export const InstagramSearchProfilesSelection: React.FC<
  InstagramSearchProfilesSelectionProps
> = ({ profiles, loading }) => {
  const { t } = useTranslation();
  const selection = useInstagramSearch((state) => state.selection);
  const setSelected = useInstagramSearch((state) => state.setSelected);
  const onClear = useInstagramSearch((state) => state.clearSelection);
  const [isSticky, setIsSticky] = useState(false);

  const onSelect = () => {
    if (selection.size !== profiles.length) {
      setSelected(profiles.map((profile) => profile.id));
    } else {
      onClear();
    }
  };

  const disabled = profiles.length === 0 && selection.size === 0;

  return (
    <div
      className={clsx(
        styles.selection,
        isSticky && styles.sticky,
        disabled && styles.disabled,
      )}
    >
      {selection.size > 0 && (
        <InstagramSearchSelectionCount
          count={selection.size}
          onClear={onClear}
        />
      )}
      <div className={styles.content}>
        <InstagramSearchSelectionDownload profiles={selection} />
        <InstagramSearchListsAssignMenu
          type="assign"
          icon="plus_24px_regular"
          text={
            selection.size === 0
              ? t("selection.addAllToList")
              : t("selection.addToList")
          }
          color="grey"
          size="sm"
          profiles={selection}
        />
      </div>
      <InstagramSearchSelectionSelect
        selected={selection.size}
        count={profiles.length}
        loading={loading}
        onClick={onSelect}
      />
      <StickyDetector className={styles.detector} onChange={setIsSticky} />
    </div>
  );
};

interface InstagramSearchListsSelectionProps {
  lists: InstagramSearch.List[];
}

export const InstagramSearchListsSelection: React.FC<
  InstagramSearchListsSelectionProps
> = ({ lists }) => {
  const [isSticky, setIsSticky] = useState(false);
  const listsSelection = useInstagramSearch((state) => state.listsSelection);
  const setListsSelected = useInstagramSearch(
    (state) => state.setListsSelected,
  );
  const onClear = useInstagramSearch((state) => state.listsClearSelection);

  const listsWithProfiles = lists.filter((list) => list.profiles.size > 0);

  const onSelect = () => {
    if (listsSelection.size !== listsWithProfiles.length) {
      setListsSelected(listsWithProfiles.map((list) => list.id));
    } else {
      onClear();
    }
  };

  const selectedLists = lists.filter((list) => listsSelection.has(list.id));

  return (
    <div className={clsx(styles.selection, isSticky && styles.sticky)}>
      {selectedLists.length > 0 && (
        <InstagramSearchSelectionCount
          count={selectedLists.length}
          onClear={onClear}
        />
      )}
      <div className={styles.content}>
        <InstagramSearchListsSelectionDownload lists={selectedLists} />
      </div>
      <InstagramSearchSelectionSelect
        selected={selectedLists.length}
        count={lists.length}
        onClick={onSelect}
      />
      <StickyDetector className={styles.detector} onChange={setIsSticky} />
    </div>
  );
};

interface InstagramSearchListSelectionProps {
  listId: InstagramSearch.List["id"];
  profiles: InstagramSearch.Profile[];
  loading: boolean;
}

export const InstagramSearchListSelection: React.FC<
  InstagramSearchListSelectionProps
> = ({ listId, profiles, loading }) => {
  const selection = useInstagramSearch((state) => state.selection);
  const setSelected = useInstagramSearch((state) => state.setSelected);
  const onClear = useInstagramSearch((state) => state.clearSelection);
  const [isSticky, setIsSticky] = useState(false);

  const onSelect = () => {
    if (selection.size !== profiles.length) {
      setSelected(profiles.map((profile) => profile.id));
    } else {
      onClear();
    }
  };

  const disabled = profiles.length === 0 && selection.size === 0;

  return (
    <div
      className={clsx(
        styles.selection,
        isSticky && styles.sticky,
        disabled && styles.disabled,
      )}
    >
      {selection.size > 0 && (
        <InstagramSearchSelectionCount
          count={selection.size}
          onClear={onClear}
        />
      )}
      <div className={styles.content}>
        <InstagramSearchListDownload listId={listId} profiles={selection} />
      </div>
      <InstagramSearchSelectionSelect
        selected={selection.size}
        count={profiles.length}
        loading={loading}
        onClick={onSelect}
      />
      <StickyDetector className={styles.detector} onChange={setIsSticky} />
    </div>
  );
};
